import { useCallback } from "react";
import { setStoredString, useStoredString } from "../../shared/storage/storedString";

export interface SizeBounds {
  /** Nothing stored yet, or nothing readable: this. */
  readonly initial: number;
  readonly min: number;
  readonly max: number;
}

function sizeOf(stored: string | null, { initial, min, max }: SizeBounds): number {
  const text = stored?.trim() ?? "";
  const size = text === "" ? Number.NaN : Number(text);
  return Number.isFinite(size) ? Math.min(max, Math.max(min, size)) : initial;
}

/**
 * A size the user sets by a resize handle, such as a sidebar's width (F138), kept in local
 * storage under `storageKey` so it stays as they left it, within its bounds. Every view with the
 * same key follows a change at once, such as both panes' sidebars.
 */
export function useStoredSize(storageKey: string, bounds: SizeBounds): readonly [number, (size: number) => void] {
  const size = sizeOf(useStoredString(storageKey), bounds);
  const setSize = useCallback((next: number) => { setStoredString(storageKey, String(next)); }, [storageKey]);
  return [size, setSize] as const;
}
